import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'aha-components';
import { isMobile } from '@/biz/tool';
import './ModalShell.css';

export interface ModalShellProps {
  open: boolean;
  mounted: boolean;
  onClose: () => void;
  title: string;
  closeLabel?: string;
  /** 关闭按钮图标，由宿主从各自 icon 目录注入 */
  closeIcon: React.ReactNode;
  children?: React.ReactNode;
}

const Header: React.FC<{
  title: string;
  closeLabel: string;
  onClose: () => void;
  closeIcon: React.ReactNode;
  mobile: boolean;
}> = ({ title, closeLabel, onClose, closeIcon, mobile }) => (
  <div
    className="w-full border-b border-[#E8E8EC] bg-white flex-shrink-0"
    style={{ paddingTop: mobile ? 'env(safe-area-inset-top, 0px)' : 0 }}
  >
    <div className="h-14 px-6 flex justify-between items-center">
      <div className="justify-start text-black text-xl font-bold leading-relaxed">
        {title}
      </div>
      <Button
        type="secondary"
        size="sm"
        htmlType="button"
        icon={closeIcon}
        iconPosition="right"
        className="tim-modal-close-button keep-px shrink-0"
        style={{
          fontFamily: '"DM Dialog Inter", sans-serif',
          color: '#404040',
          border: '1px solid #d4d4d4',
          boxShadow: 'none'
        }}
        aria-label={`${closeLabel} ${title}`}
        onClick={onClose}
      >
        {closeLabel}
      </Button>
    </div>
  </div>
);

const SHELL_Z_INDEX = 2147483011;
const TRANSITION = '240ms ease';

/**
 * IM 宿主使用常驻 fixed shell：
 * - 桌面端：全屏面板，从底部滑入
 * - 移动端：全屏面板，宿主负责 header/close 与 safe-area
 * - 关闭时仅通过 transform / opacity 隐藏，不销毁 iframe
 */
const ModalShell: React.FC<ModalShellProps> = ({
  open,
  mounted,
  onClose,
  title,
  closeLabel = 'Close',
  closeIcon,
  children
}) => {
  const mobile = isMobile();
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    if (!mounted || !open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mounted, open]);

  useEffect(() => {
    if (!mounted || !open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mounted, open, onClose]);

  if (!mounted || !portalReady) return null;

  const shellStyle: React.CSSProperties = mobile
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        maxWidth: '100dvw',
        height: '100vh',
        maxHeight: '100dvh',
        zIndex: SHELL_Z_INDEX,
        pointerEvents: open ? 'auto' : 'none',
        overflow: 'hidden'
      }
    : {
        position: 'fixed',
        inset: 0,
        zIndex: SHELL_Z_INDEX,
        pointerEvents: open ? 'auto' : 'none'
      };

  const panelStyle: React.CSSProperties = mobile
    ? {
        position: 'absolute',
        inset: 0,
        backgroundColor: '#fff',
        transform: open ? 'translateY(0)' : 'translateY(calc(100% + 16px))',
        opacity: open ? 1 : 0,
        transition: `transform ${TRANSITION}, opacity ${TRANSITION}`,
        pointerEvents: open ? 'auto' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }
    : {
        position: 'absolute',
        inset: 0,
        backgroundColor: '#fff',
        boxShadow: '0 8px 32px rgba(28, 32, 36, 0.18)',
        transform: open ? 'translateY(0)' : 'translateY(calc(100% + 24px))',
        opacity: open ? 1 : 0,
        transition: `transform ${TRANSITION}, opacity ${TRANSITION}`,
        pointerEvents: open ? 'auto' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      };

  return createPortal(
    <div
      aria-hidden={!open}
      style={shellStyle}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: open
            ? 'rgba(28, 32, 36, 0.18)'
            : 'rgba(28, 32, 36, 0)',
          transition: `background-color ${TRANSITION}`
        }}
      />
      <section
        role="dialog"
        aria-modal={open}
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        style={panelStyle}
      >
        <Header
          title={title}
          closeLabel={closeLabel}
          onClose={onClose}
          closeIcon={closeIcon}
          mobile={mobile}
        />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            paddingBottom: mobile ? 'env(safe-area-inset-bottom, 0px)' : 0,
            boxSizing: 'border-box'
          }}
        >
          {children}
        </div>
      </section>
    </div>,
    document.body
  );
};

export default ModalShell;
